// `/app/b/:siteId/setup` (WP3 Task 11) — the onboarding wizard SHELL. Task 7
// built `useOnboarding`; Task 8 built FoundationStep; Task 9 built
// VisibilityStep; Task 10 built ReputationStep + PulseStep. Every one of
// those is a BODY only — no chrome, no step order, no resume/skip/finish
// logic. This file owns all of that: it composes the four bodies inside
// `@pageboss/ui`'s `WizardStep` chrome, resolves which step is active,
// drives Continue/Skip through `useOnboarding`, and navigates to the
// overview on finish.
//
// ACTIVE STEP RESOLUTION (brief mechanics, exact order):
//   1. `?step=` when it names a real wizard step — always wins, even over a
//      state that's already moved past it (lets `?step=reputation` jump
//      back).
//   2. `state === null` (fresh door arrival, before any save has ever
//      landed) — step 1 (foundation). The first Continue/Skip PUT lazily
//      enrolls the site (Task 4's job, not this file's).
//   3. `firstIncompleteStep(state)` — the normal resume case.
//   4. Everything visited, nothing incomplete, but the wizard itself isn't
//      finished yet (`completedAt` still null) — a summary card with its
//      own "finish setup" button. This is a DISTINCT render path from
//      "Continue on the last step", which auto-finishes instead (below);
//      it only shows up on a fresh page load that lands on a
//      fully-stepped-but-never-finished site (e.g. the owner closed the tab
//      right before the finish POST fired).
//
// UNIFORM STEP-BODY CONTRACT (Task 11 review, carried item 1): every body
// is invoked with exactly `{ siteId, registerSave }`. Reputation/Pulse
// already used this shape (Task 10) and genuinely register a save.
// Foundation/Visibility's own writes ride their own flows (BusinessInfo-
// Panel's PATCH, LocationPicker's POST, the search-terms PUT) — passing
// `registerSave` to them is harmless (Foundation's body never calls it,
// Visibility calls it with a no-op resolve), and it's what keeps this
// file's composition one switch instead of four bespoke branches.
//
// SAVE-REF RESET (readiness-gate corollary): `saveRef` lives in THIS
// component and outlives any one step body's mount/unmount, so it's reset
// to a default no-op resolve every time the active step changes — otherwise
// a step that never calls `registerSave` (Foundation) would inherit
// whatever the PREVIOUS step last registered. Reputation/Pulse's own fix
// (registering only after their prefs-GET settles, not on mount) is the
// other half of the same race: a fast Continue click on THOSE two steps
// before their GET resolves finds nothing registered yet either way, so it
// awaits this default no-op and writes nothing — see reputation-step.tsx /
// pulse-step.tsx headers and reputation-pulse.test.tsx's "readiness gate"
// pin.
//
// CONTINUE = await the registered save() → `completeStep(step, false)` →
// advance. SKIP = `completeStep(step, true)` → advance, no save at all
// (skipped work is picked up later by WP1's path engine reading live
// signals — no data-loss risk by construction). ADVANCE reads the freshly
// returned state: if another step is still pending, jump `?step=` there;
// if none is, the wizard just became complete — call `finish()` and
// navigate straight to the overview (this is what makes Pulse's Continue
// double as "finish setup" without a separate button/path).
//
// `?claimed=1`: reuses WP1 Task 12's `ClaimedBanner` verbatim (same
// component overview.tsx mounts) — it owns its own single-strip mechanism
// via `consumeArrival()`, so this file does nothing extra for it beyond
// rendering the component.
//
// ESCAPE HATCH: a persistent "finish later" link to the overview, no event,
// no dismissal state — the overview's own resume chip (Task 12, WP1) self-
// heals from onboarding state, so leaving mid-wizard is always safe.
//
// UNDER-5-MINUTES GUARD: this shell adds no blocking behavior beyond a
// step's own registered save — Visibility's scan enqueue is already
// fire-and-forget inside VisibilityStep itself (Task 9), so Continue there
// resolves as fast as the schedule PUT, not the scan.
//
// `onDirty` (Foundation's prop): NOT consumed here — Task 8 left it in
// place with provisional "any click" semantics and this shell has nothing
// useful to do with a coarse dirty signal on a wizard whose Continue is
// already the explicit action. Ignored deliberately.
//
// Fix round 1 (adversarial review, HIGH + 2 MEDIUM):
//   - The whole Continue/Skip/Finish chain is now wrapped in try/catch. A
//     `completeStep`/registered-save failure renders an honest inline
//     "couldn't save this step — try again" (`role="alert"`) with the step
//     still fully rendered/interactive for retry — T7's `useOnboarding`
//     already keeps `state` unchanged on a rejected mutator, so a retry is
//     genuinely the same operation again, not a guess. A `finish()` failure
//     is handled separately: the spec's one named error code,
//     `steps_pending` (400), gets its own required copy ("one step didn't
//     save — go back"); anything else falls back to the same generic copy.
//     `saveError` resets to null both on a fresh attempt and on any step
//     change (folded into the existing active-step reset effect).
//   - In-flight re-entry guard: `pendingRef` (not React state, so it needs
//     no extra render) short-circuits a second Continue/Skip/Finish while
//     one is already running — a double-click can't fire `finish()` or
//     `navigate()` twice. Buttons are intentionally left enabled rather
//     than passed a `disabled` prop (`WizardStep` has no such prop, and
//     extending its public `@pageboss/ui` surface is out of this task's
//     scope) — the ref makes a second click a no-op instead.
import { Button } from '@/components/ui/button';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { WizardStep } from '@pageboss/ui';
import { WIZARD_STEPS, firstIncompleteStep, type LayerKey } from '@pageboss/shared';
import { ClaimedBanner } from '@/components/claimed-banner';
import { isHandledSaveError } from '@/lib/handled-save-error';
import { useOnboarding } from '@/hooks/use-onboarding';
import { locationRoutes } from '@/lib/route-map';
import type { ApiError } from '@/lib/api';
import { FoundationStep } from './setup/foundation-step';
import { VisibilityStep } from './setup/visibility-step';
import { ReputationStep } from './setup/reputation-step';
import { PulseStep } from './setup/pulse-step';

const GENERIC_SAVE_ERROR = "couldn't save this step — try again";
const STEPS_PENDING_ERROR = "one step didn't save — go back";

function finishErrorMessage(err: unknown): string {
  const e = err as Partial<ApiError> | null | undefined;
  const body = e && typeof e === 'object' ? e.body : null;
  const code =
    body && typeof body === 'object' && 'error' in body
      ? String((body as { error: unknown }).error)
      : null;
  if (e?.status === 400 && code === 'steps_pending') {
    return STEPS_PENDING_ERROR;
  }
  return GENERIC_SAVE_ERROR;
}

// A7: sentence case, like every other title in the product. The lowercase
// spelling here was the wizard's own dialect, not the house voice.
const TITLES: Record<LayerKey, string> = {
  foundation: 'Confirm your business info',
  visibility: 'Get found on Google',
  reputation: 'Stay on top of your reviews',
  pulse: 'Hear from your customers',
};

type RegisterSave = (save: () => Promise<void>) => void;

function isWizardStep(value: string | null): value is LayerKey {
  return !!value && (WIZARD_STEPS as readonly string[]).includes(value);
}

function StepBody({
  step,
  siteId,
  registerSave,
}: {
  step: LayerKey;
  siteId: string;
  registerSave: RegisterSave;
}) {
  switch (step) {
    case 'foundation':
      return <FoundationStep siteId={siteId} registerSave={registerSave} />;
    case 'visibility':
      return <VisibilityStep siteId={siteId} registerSave={registerSave} />;
    case 'reputation':
      return <ReputationStep siteId={siteId} registerSave={registerSave} />;
    case 'pulse':
      return <PulseStep siteId={siteId} registerSave={registerSave} />;
  }
}

export function Setup() {
  const { businessId = '', locationId: siteId } = useTenantParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const onboarding = useOnboarding(siteId ?? '');

  const stepParam = searchParams.get('step');
  const readyState = onboarding.kind === 'ready' ? onboarding.state : undefined;

  // Active step (see file header for the exact resolution order). Used as a
  // dummy 'foundation' while onboarding is still loading/erroring — never
  // rendered in that branch, only read by the reset effect below so hook
  // order stays stable across every render.
  const active: LayerKey | 'summary' = isWizardStep(stepParam)
    ? stepParam
    : readyState === null
      ? WIZARD_STEPS[0]
      : readyState
        ? (firstIncompleteStep(readyState) ?? 'summary')
        : WIZARD_STEPS[0];

  const saveRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // Re-entry guard for the Continue/Skip/Finish chain (fix round 1, MEDIUM).
  // A ref, not state — flipping it must never itself trigger a render, it
  // only needs to be readable synchronously at the top of the next click.
  const pendingRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset the save ref AND any stale error on every step change — see file
  // header's SAVE-REF RESET note. Runs before hooks that must stay
  // unconditional, so it's declared ahead of the loading/error early
  // returns below.
  useEffect(() => {
    saveRef.current = () => Promise.resolve();
    setSaveError(null);
  }, [active]);

  const registerSave = useCallback<RegisterSave>((fn) => {
    saveRef.current = fn;
  }, []);

  if (!siteId) return null;

  if (onboarding.kind === 'loading') {
    return <div className="min-h-[12rem]" aria-hidden="true" />;
  }
  if (onboarding.kind === 'error') {
    return (
      <p role="alert" className="text-sm text-danger-fg">
        {onboarding.message}
      </p>
    );
  }

  const { completeStep, finish } = onboarding;
  // Re-bind as a plain string const — TS doesn't narrow `siteId` (from the
  // destructured, possibly-undefined useParams()) across the nested closures
  // below just from the early `if (!siteId) return null` guard above.
  const site = siteId;

  // Runs `finish()` and, on success, navigates to the overview. On failure
  // it sets the honest inline error (the required `steps_pending` copy, or
  // the generic fallback for anything else) and deliberately does NOT
  // rethrow — the caller's own try/catch is for completeStep/save failures
  // only, so a finish failure must be fully handled here, not bubble up and
  // get double-reported.
  async function finishAndNavigate() {
    try {
      await finish();
    } catch (err) {
      setSaveError(finishErrorMessage(err));
      return;
    }
    navigate(locationRoutes.overview(businessId, site));
  }

  async function advance(step: LayerKey, skipped: boolean) {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setSaveError(null);
    try {
      if (!skipped) await saveRef.current();
      const updated = await completeStep(step, skipped);
      const next = firstIncompleteStep(updated);
      if (next === null) {
        // Every step just resolved — this is what makes the last step's
        // Continue/Skip double as "finish setup" (brief mechanics: "Finish
        // (after the 4th step's PUT resolves)").
        await finishAndNavigate();
        return;
      }
      setSearchParams({ step: next });
    } catch (err) {
      // A rejected registered-save or completeStep PUT — the step stays
      // exactly as rendered (no navigation, no state change) so the owner
      // can just click Continue/Skip again; T7's useOnboarding leaves
      // `state` untouched on a mutator rejection, so retry is the same
      // real operation, not a no-op.
      //
      // Dup-banner fix (wp8 fix-forward list, 2026-08-21): a save that
      // already rendered its OWN specific inline error (BusinessInfoPanel,
      // SearchTermsBlock) rejects with HandledSaveError — still block the
      // advance, but don't stack the generic banner on top of the specific
      // copy the owner is already reading.
      if (!isHandledSaveError(err)) setSaveError(GENERIC_SAVE_ERROR);
    } finally {
      pendingRef.current = false;
    }
  }

  async function handleFinishFromSummary() {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setSaveError(null);
    try {
      await finishAndNavigate();
    } finally {
      pendingRef.current = false;
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <ClaimedBanner />
      {saveError ? (
        <p role="alert" className="mx-auto max-w-xl text-sm text-danger-fg">
          {saveError}
        </p>
      ) : null}
      {active === 'summary' ? (
        <div className="mx-auto max-w-xl rounded-xl border border-border-subtle bg-card p-6">
          <p className="text-sm text-text-secondary">
            You've been through every step — finish setup any time.
          </p>
          <Button type="button" onClick={() => void handleFinishFromSummary()} className="mt-4">
            Finish setup
          </Button>
        </div>
      ) : (
        <WizardStep
          step={active}
          index={WIZARD_STEPS.indexOf(active) + 1}
          total={WIZARD_STEPS.length}
          title={TITLES[active]}
          onContinue={() => void advance(active, false)}
          continueLabel={active === 'pulse' ? 'Finish setup' : 'Save and continue'}
          onSkip={() => void advance(active, true)}
        >
          <StepBody step={active} siteId={siteId} registerSave={registerSave} />
        </WizardStep>
      )}
      <Link
        to={locationRoutes.overview(businessId, siteId)}
        className="mx-auto text-sm text-text-tertiary underline underline-offset-2 hover:text-text-secondary"
      >
        Finish later
      </Link>
    </div>
  );
}
